import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Plane,
  Users,
  Calendar,
  MessageSquare,
  Plus,
  Home,
  Eye
} from 'lucide-react';

export default function DashboardTripsTab({
  bookings,
  handleOpenAddOns,
  setSelectedForumTrip,
  setActiveTab
}) {
  if (bookings.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Plane className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="font-heading text-lg font-semibold text-primary mb-2">
            No Trips Booked Yet
          </h3>
          <p className="text-muted-foreground mb-4">
            Start planning your next adventure!
          </p>
          <Link to="/trips">
            <Button className="bg-secondary hover:bg-secondary/90">
              Browse Trips
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {bookings.map((booking, index) => (
        <Card key={booking.id} className="overflow-hidden">
          {index === 0 && (
            <div className="bg-gradient-to-r from-secondary to-orange-500 p-3 text-white">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span className="text-sm font-medium">Next Upcoming Trip</span>
              </div>
            </div>
          )}
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-xl font-semibold text-primary">
                    {booking.trip_title || 'Trip'}
                  </h3>
                  <Badge className={
                    booking.status === 'confirmed' ? 'bg-green-500' :
                    booking.status === 'pending_payment' ? 'bg-yellow-500' : 'bg-gray-500'
                  }>
                    {booking.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {new Date(booking.tour_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {booking.travelers} traveler(s)
                  </span>
                  {booking.room_type === 'private' && (
                    <span className="flex items-center gap-1 text-secondary">
                      <Home className="h-4 w-4" />
                      Private Room
                    </span>
                  )}
                </div>
                
                {/* Payment Info */}
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Total</p>
                      <p className="font-semibold text-primary">${booking.total_amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Paid</p>
                      <p className="font-semibold text-green-600">${booking.amount_paid?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Remaining</p>
                      <p className="font-semibold text-orange-600">${booking.remaining_amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Plan</p>
                      <p className="font-semibold text-primary">
                        {booking.payment_plan === 'full' ? 'Paid in Full' : 
                         `${booking.total_payments} payments`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-2 min-w-[140px]">
                <Button 
                  variant="default"
                  className="bg-secondary hover:bg-secondary/90"
                  onClick={() => handleOpenAddOns(booking)}
                  data-testid={`add-excursions-${booking.id}`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Excursions
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSelectedForumTrip({
                      trip_id: booking.trip_id,
                      trip_title: booking.trip_title,
                      tour_date: booking.tour_date
                    });
                    setActiveTab('forum');
                  }}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Trip Forum
                </Button>
                <Link to={`/trips/${booking.trip_slug || booking.trip_id}`}>
                  <Button variant="ghost" className="w-full text-muted-foreground">
                    <Eye className="h-4 w-4 mr-2" />
                    View Trip
                  </Button>
                </Link>
                {/* Show booked add-ons */}
                {booking.selected_add_ons && booking.selected_add_ons.length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs text-muted-foreground mb-1">Booked Excursions:</p>
                    {booking.selected_add_ons.map(addon => (
                      <Badge key={addon.id} variant="secondary" className="text-xs mr-1 mb-1">
                        {addon.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
